// @flow

import * as React from 'react';

import range from 'lodash/range';
import memoize from 'memoize-one';
import { useDispatch } from 'react-redux';

import {
  updateProductAndGetCartOrNotify,
  selectOneProductErrors,
  type DecoratedProduct,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Fqdn, PopoverIcon } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import { selectMoreThan1YearDuration } from '../selectors';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItem from './components/ProductItem';
import { DurationRow } from './components/Rows';
import type { GandiFormSelectOption } from './types';

type Props = {
  product: DecoratedProduct,
};

const DEFAULT_VALUES = [1, 2];

const generateRange = memoize((minDuration: number, maxDuration: number) =>
  range(minDuration, maxDuration + 1),
);

export function CertificateCreate({ product }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const moreThan1YearDuration = useAppSelector(state =>
    selectMoreThan1YearDuration(state, product.payload),
  );

  const getAllPossibleDurations: () => Array<number> = () => {
    const {
      payload: { certificateDetails },
    } = product;
    if (certificateDetails?.prices) {
      const allPossibleDurations = certificateDetails.prices.reduce(
        (acc: Array<any>, price) => [
          ...acc,
          ...generateRange(price.minDuration, price.maxDuration),
        ],
        [],
      );
      return allPossibleDurations;
    }
    return DEFAULT_VALUES;
  };

  const handleDurationChange = (
    selectOption: GandiFormSelectOption<string>,
  ) => {
    const newPayload = {
      ...product.payload,
      duration: parseInt(selectOption.value, 10),
    };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  // safely get the CN saved in the product payload
  // in the certificate create wizard
  const cn = product.payload.options?.cn;
  const isFree = product.payload.options?.type === 'free';

  const price = product.computedTotalPrices?.beforeTaxes;
  const hasTaxes = product.hasTaxes;

  const duration = product.payload.duration;
  const rawPrice = product.payload.certificateDetails?.prices.find(
    priceOption =>
      duration != null &&
      duration >= priceOption.minDuration &&
      duration <= priceOption.maxDuration,
  );
  const normalPrice =
    rawPrice?.normalPriceBeforeTaxes != null && duration != null
      ? rawPrice.normalPriceBeforeTaxes * duration
      : undefined;

  const label = product.label || __('Certificate');
  const detail = cn ? <Fqdn>{cn}</Fqdn> : null;

  if (productErrors.length > 0) {
    return (
      <ProductErrorItem
        key={`${product.id}-error-row`}
        label={label}
        detail={detail}
        productId={product.id}
        errors={productErrors}
      />
    );
  }

  return (
    <ProductItem
      key={`${product.id}-row`}
      label={label}
      detail={detail}
      productId={product.id}
      priceBeforeTaxes={product.isWorking ? undefined : price}
      hasTaxes={hasTaxes}
    >
      <DurationRow
        productId={product.id}
        disabledOption={!!product.isWorking}
        // $FlowFixMe[incompatible-type-arg] - number is number|string
        onChange={handleDurationChange}
        value={product.payload.duration || 1}
        showOption={!isFree}
        values={getAllPossibleDurations()}
        total={product.isWorking ? undefined : price}
        priceCrossedOut={normalPrice}
        popoverInfo={
          moreThan1YearDuration ? (
            <PopoverIcon
              icon="warningWire"
              id={`popover-info-ssl-${product.id}`}
              behaviour="click"
            >
              {__(
                'For safety reasons, multi-years certificates need to be reissued each year.',
              )}{' '}
              <a
                href={__(
                  'https://docs.gandi.net/en/ssl/description/multi_year.html',
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {__('How does it work?')}
              </a>
            </PopoverIcon>
          ) : undefined
        }
      />
    </ProductItem>
  );
}
